import {useResizeObserver} from '@github-ui/use-resize-observer'
import {useCallback, useEffect, useRef} from 'react'

/** Read by `BoardSurface.module.css` to size the board against the viewport. */
const TOP_OFFSET_CSS_VAR = '--board-surface-top'

/**
 * Keeps `--board-surface-top` in step with the board's distance from the top of the
 * document, so the surface can size itself to the space left below whatever the page
 * renders above it. Returns the ref to put on that surface.
 *
 * The board owns its scroll containers and fills its parent, so the parent has to hand it
 * a bounded height; nothing in `@github-ui/board` can supply one, because a scroll
 * container cannot know where on a page it was placed. The chrome above this board is not
 * a fixed amount either -- a global header, a staff bar, a banner, the page header, the
 * view's description and the filter bar all sit above it, and each of them may be absent
 * -- so the offset is measured rather than written down. This is the `--app-offset`
 * pattern from `@github-ui/repo-agents` and `--sidebar-page-layout-top-offset` from
 * `@github-ui/layout-primitives`.
 *
 * Measured against the document rather than the viewport. The two agree while the page is
 * at the top, which is where a correctly sized board keeps it, and a viewport-relative
 * offset would otherwise grow the board every time the page scrolled, which scrolls the
 * page further.
 *
 * Written imperatively rather than through state: the value only feeds a height, so a
 * reflow restyles the board instead of re-rendering it, and the write cannot drive the
 * observer that prompted it back round.
 */
export function useBoardViewportHeight(): (node: HTMLElement | null) => void {
  const surfaceRef = useRef<HTMLElement | null>(null)
  const lastTopRef = useRef(Number.NaN)

  const measure = useCallback(() => {
    const surface = surfaceRef.current
    if (surface === null) return

    const top = Math.max(0, Math.round(surface.getBoundingClientRect().top + window.scrollY))
    if (top === lastTopRef.current) return

    lastTopRef.current = top
    surface.style.setProperty(TOP_OFFSET_CSS_VAR, `${top}px`)
  }, [])

  // The body, not the board: what moves the board is chrome above it appearing,
  // disappearing or reflowing, and a board that has been pushed down the page reports no
  // resize of its own.
  const observeBody = useResizeObserver(measure)
  useEffect(() => observeBody(document.body), [observeBody])

  return useCallback(
    (node: HTMLElement | null) => {
      surfaceRef.current = node
      lastTopRef.current = Number.NaN
      // On mount rather than on the first reflow, so the board is bounded on its first
      // paint and never windows against a viewport it has not got.
      measure()
    },
    [measure],
  )
}
